import React, { useEffect, useState } from "react";
import ReactGA from "react-ga4";
import styles from "./style.module.scss";
// import { ReactComponent as CheckIcon } from "../../../TextDetectionResult/assets/CheckIcon.svg";
import { ButtonPrimary, SaleBadge } from "@components";
// import { ReactComponent as CrownIcon } from '../../../pages/GenerationBlock/components/SideMenu/assets/Crown.svg';
import { useUnit } from "effector-react";
import { $pricing, pricingFx, subscribeFx } from "../../../features/pricing/model";
import RevolutCheckout from "@revolut/checkout";
import { $user, $userLanguage } from "../../../features/app/model";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { SubscriptionResponseDto } from "../../../http/Api";

import { ReactComponent as AiIcon } from "../../../assets/icons/AI.svg";
import { ReactComponent as StickIcon } from "../../../assets/icons/Stick.svg";
import { ReactComponent as StarsIcon } from '../../../assets/icons/Stars.svg';
import { ReactComponent as PhotoIcon } from "../../../assets/icons/PhotoIcon.svg";
import { ReactComponent as StarFilled } from './assets/StarFilled.svg';
import { ReactComponent as StarOutline } from './assets/StarOutline.svg';
import { ReactComponent as StarHalf } from './assets/StarHalf.svg';
import { ReactComponent as TrustpilotLogo } from './assets/TrustpilotLogo.svg';
import { ReactComponent as Stars5Icon } from "../../../assets/trustpilot/stars-5-1.svg";
import { ReactComponent as TrustpilotIconSvg } from "../../../assets/trustpilot/trustpilot-1.svg";

declare global {
	interface Window {
		Trustpilot: any;
	}
}

const PricingItem: React.FC<{
	title: string;
	price: string;
	id: string;
	features: string[];
	colorScheme?: "orange" | "basic";
	discountEnabled: boolean;
	period: string;
	currency: string;
	isSelected: boolean;
	onClick: () => void;
}> = ({
	title,
	price: actualPriceString,
	id,
	period,
	colorScheme,
	discountEnabled,
	features,
	currency,
	isSelected,
	onClick,
}) => {
	const { t } = useTranslation();

	const actualPriceNum = Number(actualPriceString);

	// Current price display logic
	let currentDisplayPrice: string;
	let currentDisplayUnitKey: string;

	// Original (strikethrough) price display logic
	let originalPriceValueForCalc: number;
	let originalDisplayUnitKey: string;

	if (period === "year") {
		const monthlyEquivalent = actualPriceNum / 12;
		currentDisplayPrice = monthlyEquivalent.toFixed(2);
		currentDisplayUnitKey = "pricing.month";

		originalPriceValueForCalc = monthlyEquivalent * 2.5; // 60% more than the displayed monthly price
		originalDisplayUnitKey = "pricing.month"; // Unit is also per month for strikethrough
	} else if (period === "month") {
		currentDisplayPrice = actualPriceNum.toFixed(2);
		currentDisplayUnitKey = "pricing.month";

		originalPriceValueForCalc = actualPriceNum * 2.5; // User's 2.5x factor for full monthly price
		originalDisplayUnitKey = "pricing.month";
	} else {
		// 'day' (weekly)
		currentDisplayPrice = actualPriceNum.toFixed(2);
		currentDisplayUnitKey = "pricing.week";

		originalPriceValueForCalc = actualPriceNum * 2.5; // User's 2.5x factor for full weekly price
		originalDisplayUnitKey = "pricing.week";
	}

	const finalOriginalDisplayPrice = originalPriceValueForCalc.toFixed(2);
	const titleParts = title.split(' ');

	return (
		<div
			onClick={onClick}
			className={`${styles.PricingItem} ${
				period === "day" ? styles["PricingItem--weekly"] : ""
			} ${isSelected ? styles.PricingItem__selected : ""}`}
		>
			{period === 'month' && (
				<div className={styles.PricingItem__mostPopularBadge}>Most popular</div>
			)}
			{period === 'year' && (
				<>
					<div className={styles.PricingItem__discountBadge}>Save 50% 🔥</div>
					<div className={styles.PricingItem__bestOfferBadge}>✓ Best value</div>
				</>
			)}
			{period === 'day' && (
				<div className={styles.PricingItem__topBanner}>{t("pricing.weekly")}</div>
			)}
			<div className={styles.PricingItem__contentWrapper}>
				<div className={styles.PricingItem__titleContainer}>
					<div className={styles.PricingItem__selectionIndicator}>
						<div className={isSelected ? styles.PricingItem__selectionIndicator_checked : undefined}></div>
					</div>
					<h2 style={{ whiteSpace: 'nowrap' }}>{title}</h2>
				</div>
				<div className={styles.PricingItem__priceLine}>
					<div className={styles.PricingItem__priceContainer}>
						<span className={styles.PricingItem__priceValue}>{currency}{currentDisplayPrice}</span>
						<span className={styles.PricingItem__pricePeriod}>/&nbsp;{t(currentDisplayUnitKey)}</span>
						<span className={styles.PricingItem__originalPrice}>
							{currency}{finalOriginalDisplayPrice}
						</span>
					</div>

					{period === "year" && (
						<div className={styles.PricingItem__billedAnnually}>
							<span>Billed </span>
							<strong>{`${currency}${(Number(currentDisplayPrice) * 12).toFixed(0)}`}</strong>
							<span> Annually</span>
						</div>
					)}
					{period === "day" && (
						<div className={styles.PricingItem__billedAnnually}>
							<span>then $39.99 per month</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export const PricingBlock: React.FC<{ onUpgrade?: () => void }> = () => {
	const user = useUnit($user);
	const currLang = useUnit($userLanguage);
	const [data, getSubscriptions, getSubscriptionsPending] = useUnit([$pricing, pricingFx, pricingFx.pending]);
	const subscribeQuery = useUnit(subscribeFx);
	const navigate = useNavigate();

	const [subscriptionsCurrent, setSubscriptionsCurrent] = useState<SubscriptionResponseDto[]>([]);
	const [selectedPlan, setSelectedPlan] = useState<SubscriptionResponseDto | null>(null);

	const { t } = useTranslation();

	const rating = 4.7; // Example rating

	const renderStars = (rating: number) => {
		const stars = [];
		for (let i = 1; i <= 5; i++) {
			if (i <= rating) {
				stars.push(<StarFilled key={i} />);
			} else if (i - rating < 1) {
				stars.push(<StarHalf key={i} />);
			} else {
				stars.push(<StarOutline key={i} />);
			}
		}
		return stars;
	};

	const features: { icon?: JSX.Element; titleKey: string; descriptionKey: string }[] = [
		{ icon: <AiIcon />, titleKey: "pricing.features.detector.title", descriptionKey: "pricing.features.detector.description" },
		{ icon: <StickIcon />, titleKey: "pricing.features.optimizer.title", descriptionKey: "pricing.features.optimizer.description" },
		{ icon: <PhotoIcon />, titleKey: "pricing.features.photoSolver.title", descriptionKey: "pricing.features.photoSolver.description" },
		{ icon: <StarsIcon />, titleKey: "pricing.features.chat.title", descriptionKey: "pricing.features.chat.description" },
	];

	useEffect(() => {
        const trustpilot_widget = document.getElementById("trustpilot-widget");
        if (window.Trustpilot && trustpilot_widget) {
          window.Trustpilot.loadFromElement(trustpilot_widget);
        }
    }, []);

	useEffect(() => {
		if (localStorage.getItem("i18nextLng") !== null) {
			const currentLang = localStorage.getItem("i18nextLng")?.toLowerCase();
			getSubscriptions({
				languageCode: currentLang?.toUpperCase() || "EN",
			});
		}
	}, [currLang, getSubscriptions]);

	useEffect(() => {
		if (data) {
			const subscriptions = (data as SubscriptionResponseDto[])
				.filter((s) => s.isPricingPage)
				.sort((a, b) => {
					const periodOrder: { [key: string]: number } = { month: 1, day: 2, year: 3 };
					return (periodOrder[a.period] || 99) - (periodOrder[b.period] || 99);
				});
			setSubscriptionsCurrent(subscriptions);
			
			// Set default selection
			const weeklyPlan = subscriptions.find(s => s.period === 'day');
			if (weeklyPlan) {
				setSelectedPlan(weeklyPlan);
			} else if (subscriptions.length > 0) {
				const defaultPlan = subscriptions.find(s => s.period === 'month') || subscriptions[0];
				setSelectedPlan(defaultPlan);
			}
		}
	}, [data]);

	const handleGetStarted = async () => {
		if (!selectedPlan) return;

        console.log("Checking user state before purchase:", user);

		if (!user || !user.userId) {
			navigate("/auth");
			return;
		}

		const planData = {
			subscriptionId: selectedPlan.subscriptionId,
			title: selectedPlan.title,
			amount: selectedPlan.amount,
			currency: {
				symbol: selectedPlan.currency?.symbol,
				code: 'USD',
			},
		}

		ReactGA.event('initiate_checkout', {
			currency: planData.currency.code,
			value: 0,
			items: [
				{
					item_id: planData.subscriptionId,
					item_name: planData.title,
					price: planData.amount,
				},
			],
		})
		window.dataLayer.push({
			event: 'ads_initiate_checkout',
			value: 0,
			currency: planData.currency.code,
			items: [
				{
					item_id: planData.subscriptionId,
					item_name: planData.title,
					price: planData.amount,
				},
			],
			user_email: user?.email,
		})

		try {
			ReactGA.event({
				category: "Button",
				action: "Click",
				label: "Get access",
			});

			const orderResponse = await subscribeQuery({
				subscriptionId: selectedPlan.subscriptionId,
				frontendUrl: window.location.origin,
			});

			if (!orderResponse) {
				throw new Error('Failed to create payment session')
			}

			if (orderResponse.sessionUrl) {
				localStorage.setItem(
					'pendingPurchasePlan',
					JSON.stringify({ ...planData, sessionId: orderResponse.sessionId }),
				)
				window.location.href = orderResponse.sessionUrl
			}
			else if (orderResponse.token) {
				const { payWithPopup } = await import("@revolut/checkout").then(m => m.default(
					orderResponse.token!,
					process.env.REACT_APP_REVOLUT_TYPE as "sandbox" | "prod" | "dev",
				));

				payWithPopup({
					onSuccess() {
						const purchaseParams: any = {
							transaction_id: orderResponse.sessionId,
							value: selectedPlan.amount,
							currency: selectedPlan.currency?.code || 'USD',
							items: [
								{
									item_id: selectedPlan.subscriptionId,
									item_name: selectedPlan.title,
									price: selectedPlan.amount,
								},
							],
						}
						ReactGA.event('purchase', purchaseParams)
						if (window.dataLayer) {
							window.dataLayer.push({
								event: 'ads_purchase',
								transaction_id: purchaseParams.transaction_id,
								value: purchaseParams.value,
								currency: purchaseParams.currency,
								user_email: user?.email,
							})
						}
						// ⭐ НОВОЕ: Mark as tracked BEFORE navigate to prevent App.tsx from sending duplicate
						sessionStorage.setItem('ads_purchase_tracked_new_sub', 'true');
						console.log('🚀 Fired ads_purchase event for Revolut in Landing/PricingBlock');
						// ⭐ конец нового
						window.alert(t('pricing.alert.thanks'))
						navigate('/?new_subscription=true')
					},
					onError(error) {
						window.alert(
							`${t("pricing.alert.error")} ${error.toString()}`
						);
					},
					savePaymentMethodFor: 'merchant',
				})
			} else {
				throw new Error("Invalid payment response format from backend");
			}
		} catch (error) {
			console.error("Payment initiation error:", error);
			window.alert(t("pricing.alert.error"));
		}
	};

	return (
		<div className={`app-container ${styles.PricingBlock}`}>
			<div className={styles.Pricing}>
				<h2 className={styles.Pricing__title}>{t("pricing.title")}</h2>

				<div className={styles.Pricing__guarantee}>{t("pricing.guarantee")}</div>
				<div className={styles.Pricing__container}>
					{subscriptionsCurrent?.length
						? subscriptionsCurrent.map((el: SubscriptionResponseDto, index: number) => (
								<PricingItem
									key={el.subscriptionId}
									discountEnabled={el.period === 'month'}
									title={el.title || ""}
									price={el.amount ? el.amount.toFixed(2) : "0.00"}
									id={el.subscriptionId}
									period={el.period}
									features={el.features || []}
									currency={el.currency?.symbol || ""}
									isSelected={selectedPlan?.subscriptionId === el.subscriptionId}
									onClick={() => setSelectedPlan(el)}
								/>
						  ))
						: "No pricing plans available"}
				</div>
				<div className={styles.Pricing__getStartedButtonContainer}>
					<ButtonPrimary
						onClick={handleGetStarted}
						disabled={!selectedPlan}
						colorScheme="orange"
						textAlign="center"
						className={styles.Pricing__getStartedButton}
					>
						{t("pricing.getAccess")}
					</ButtonPrimary>
				</div>
				<div className={styles.TrustpilotWrapper}>
					<strong>Excellent</strong>
					<Stars5Icon />
					<TrustpilotIconSvg />
				</div>
				<div className={styles.Pricing__cancellation}>{t("pricing.cancellation")}</div>
				<div className={styles.Features}>
					<h3 className={styles.Features__title}>{t("pricing.allPlansInclude")}</h3>
					<div className={styles.Features__list}>
						{features.map((feature, index) => (
							<div key={index} className={styles.Features__item}>
								<div className={styles.Features__iconWrapper}>{feature.icon}</div>
								<div className={styles.Features__textWrapper}>
									<h4>{t(feature.titleKey)}</h4>
									<span>{t(feature.descriptionKey)}</span>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};
